import type { DiagnostikaPosteZaPrikaz, IzidPreizkusaPoste } from '@koda/shared';
import { useState, type JSX } from 'react';

import { api } from '../lib/api';

/** Kaj je nastavljeno; vrednosti so javne (skrivnosti strežnik ne vrne). */
function Nastavitve({ d }: { d: DiagnostikaPosteZaPrikaz }): JSX.Element {
  return (
    <ul className="drobno nastavitve-poste">
      <li>pošiljatelj: {d.posiljatelj ?? '—'}</li>
      <li>obseg (scope): {d.obseg}</li>
      <li>naslov sendMail: {d.naslovSendMail ?? '—'}</li>
      <li>tenant: {d.tenantId ?? '—'}</li>
      <li>client ID: {d.clientId ?? '—'}</li>
      <li>skrivnost: {d.skrivnostNastavljena ? 'nastavljena' : 'ni nastavljena'}</li>
      {d.zeton !== null && (
        <>
          <li>žeton za: {d.zeton.aud ?? '—'}</li>
          <li>
            dovoljenja v žetonu (roles):{' '}
            {d.zeton.roles.length > 0 ? d.zeton.roles.join(', ') : 'brez dovoljenj'}
          </li>
        </>
      )}
    </ul>
  );
}

/**
 * Preizkus pošiljanja in surov odgovor Microsofta.
 *
 * Namesto naše razlage pokaže, kar je res prišlo: HTTP status, `code`,
 * `message` in `request-id`, pri žetonu pa kodo AADSTS. Dovoljenja v žetonu
 * (`roles`) povedo, ali je `Mail.Send` res dodeljen aplikaciji.
 */
export function PreizkusPoste(): JSX.Element {
  const [izid, setIzid] = useState<IzidPreizkusaPoste | null>(null);
  const [diagnostika, setDiagnostika] = useState<DiagnostikaPosteZaPrikaz | null>(null);
  const [tece, setTece] = useState(false);
  const [napaka, setNapaka] = useState<string | null>(null);

  const preveri = (): void => {
    setTece(true);
    setNapaka(null);
    setIzid(null);
    api
      .diagnostikaPoste()
      .then(setDiagnostika)
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Diagnostike ni bilo mogoče prebrati.');
      })
      .finally(() => {
        setTece(false);
      });
  };

  const poslji = (): void => {
    setTece(true);
    setNapaka(null);
    api
      .preizkusPoste()
      .then((odgovor) => {
        setIzid(odgovor);
        setDiagnostika(odgovor.diagnostika);
      })
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Preizkus ni uspel.');
      })
      .finally(() => {
        setTece(false);
      });
  };

  const zapis = izid?.zapis ?? null;

  return (
    <section className="kartica">
      <h2>Preizkus pošiljanja</h2>
      <p className="namig">
        Pošlje poskusno sporočilo na vaš naslov in izpiše natanko to, kar vrne Microsoft — brez
        naše razlage.
      </p>

      <div className="gumbi">
        <button type="button" className="gumb" onClick={poslji} disabled={tece}>
          {tece ? 'Pošiljam …' : 'Pošlji poskusno sporočilo'}
        </button>
        <button type="button" className="povezava" onClick={preveri} disabled={tece}>
          Samo preveri nastavitve
        </button>
      </div>

      {napaka !== null && <p className="napaka">{napaka}</p>}

      {izid !== null && izid.poslano && (
        <p className="uspeh-besedilo">Poslano na {izid.za}. Preveri nabiralnik.</p>
      )}
      {izid !== null && !izid.poslano && (
        <p className="napaka">Pošiljanje na {izid.za} ni uspelo.</p>
      )}

      {izid?.napaka != null && (
        <ul className="drobno nastavitve-poste">
          <li>faza: {izid.napaka.faza === 'zeton' ? 'pridobivanje žetona' : 'pošiljanje'}</li>
          <li>HTTP: {izid.napaka.status ?? '—'}</li>
          <li>code: {izid.napaka.koda ?? '—'}</li>
          <li>message: {izid.napaka.sporocilo ?? '—'}</li>
          <li>request-id: {izid.napaka.zahtevaId ?? '—'}</li>
          <li>naslov: {izid.napaka.naslov}</li>
        </ul>
      )}

      {zapis !== null && <pre className="surovo">{zapis}</pre>}

      {diagnostika !== null && (
        <>
          <h3 className="podnaslov-manjsi">Nastavitve</h3>
          {!diagnostika.nastavljeno && (
            <p className="napaka drobno">Manjka katera od spremenljivk GRAPH_*.</p>
          )}
          <Nastavitve d={diagnostika} />
          {diagnostika.napaka !== null && (
            <pre className="surovo">
              {[
                `faza: ${diagnostika.napaka.faza}`,
                `HTTP: ${String(diagnostika.napaka.status ?? 0)}`,
                `code: ${diagnostika.napaka.koda ?? '-'}`,
                `message: ${diagnostika.napaka.sporocilo ?? '-'}`,
                `request-id: ${diagnostika.napaka.zahtevaId ?? '-'}`,
                `naslov: ${diagnostika.napaka.naslov}`,
                diagnostika.napaka.telo ? `odgovor: ${diagnostika.napaka.telo}` : '',
              ]
                .filter((v) => v !== '')
                .join('\n')}
            </pre>
          )}
        </>
      )}
    </section>
  );
}
